import { useMemo } from "react";
import { Space, Table, Typography } from "antd";
import type { TableProps } from "antd";

import { Value } from "@components/Text";
import { getCurrencyValue } from "@components/Amount";
import { getTransactionTypeLabel } from "@components/Transaction";

import type { Transaction, InvestorFile } from "./types";

const FILE_ID_PREFIX = "FIL_";

const LABEL_DOCUMENT = "Document";
const LABEL_TRANSACTION = "Transaction";

// NOTE: a row is either an investor file or a transaction; the optional
//       fields tell the two apart.
type Attachment = {
  id: string;
  date: string;
  type?: string;
  amount?: number;
  batchId?: string;
  investmentId?: string;
  documentType?: string;
};

type Props = {
  files: InvestorFile[];
  transactions: Transaction[];
  investmentId: string;
  getBatchName: (batchId: string) => string | undefined;
};

const InvestmentAttachmentsTable = ({
  files,
  transactions,
  investmentId,
  getBatchName,
}: Props) => {
  const dataSource = useMemo((): Attachment[] =>
    [ ...files, ...transactions ]
      .filter((item) => investmentId === item.investmentId)
  , [ files, transactions, investmentId ]);

  const columns: TableProps<Attachment>["columns"] = [
    {
      key: "id",
      title: "Attachment",
      dataIndex: "id",
      render: (id: string) => (id.startsWith(FILE_ID_PREFIX) ? LABEL_DOCUMENT : LABEL_TRANSACTION),
    },
    {
      key: "type",
      title: "Type",
      dataIndex: "type",
      render: (type: string, item) =>
        !item.documentType
          ? getTransactionTypeLabel(type)
          : (
            <Space>
              <Typography.Text strong>{item.documentType}</Typography.Text>
              (Transferred)
            </Space>
          ),
    },
    {
      key: "details",
      title: "Details",
      dataIndex: "details",
      render: (details, item) => (item.batchId ? getBatchName(item.batchId) : getCurrencyValue(item.amount)),
    },
    {
      key: "date",
      title: "Date",
      dataIndex: "date",
      render: (value) => <Value value={value} />,
    },
  ];

  const tableProps: TableProps<Attachment> = {
    size: "small",
    rowKey: "id",
    pagination: false,
    columns,
    dataSource,
  };

  return (
    <Table {...tableProps} />
  );
};

export default InvestmentAttachmentsTable;
